import { useEffect, useState } from "react";
import { Spinner } from "react-bootstrap";
import { Helmet } from "react-helmet-async";
import { useSelector } from "react-redux";
import { useLocation, useNavigate } from "react-router-dom";
import { useTranslate } from "../../context/TranslationContext";
import { useGetCurrentSubscriptionQuery } from "../../redux/Apis/Packages";
import { useGetCurrentPSSQuery } from "../../redux/Apis/PSSWorkSpace";

const PurchaseConfirmation = () => {
  const { t } = useTranslate("purchaseConfirmation");
  const lang = useSelector((state) => state.language.lang);
  const navigate = useNavigate();
  const location = useLocation();
  const isMembershipRoute = location.pathname.includes("success-membership");
  const isPSSRoute = location.pathname.includes("success-pss");

  const {
    data,
    isLoading,
    error,
    // refetch: refetchSubscription,
  } = useGetCurrentSubscriptionQuery(
    { lang },
    { refetchOnMountOrArgChange: isMembershipRoute },
  );

  const {
    data: dataPSS,
    isLoading: isLoadingPSS,
    error: errorPSS,
    // refetch: refetchPSS,
  } = useGetCurrentPSSQuery(
    { lang },
    { refetchOnMountOrArgChange: !isMembershipRoute },
  );

  const [content, setContent] = useState(null);

  // useEffect(() => {
  //   if (isMembershipRoute) refetchSubscription();
  //   if (isPSSRoute) refetchPSS();
  // }, [isMembershipRoute, isPSSRoute, refetchSubscription, refetchPSS]);

  useEffect(() => {
    if (isMembershipRoute && data?.success && data?.data?.package) {
      const pkg = data.data.package;

      setContent({
        title: `${t("welcome")} ${pkg.title}!`,
        details: `${t("subscribedDetails")} ${pkg.title} – £${pkg.price}/${
          pkg.interval
        }`,
        terms: `${t("termsMembership")}`,
        link: { text: t("termsLinkText"), url: "/refund-policy" },
        steps: [
          `Upload up to ${pkg.course_limit} tracks or courses`,
          ...pkg.features,
        ],
        cta: { label: t("ctaDashboard"), actionKey: "dashboard" },
      });
    }

    if (isPSSRoute && dataPSS?.success && dataPSS?.data?.product) {
      const product = dataPSS.data.product;
      const templateId = product._id;
      const price = product.pricing;

      setContent({
        title: `${product.title_hook || product.title} ${t("pssConfirmedTitle")}`,
        details: `${t("pssDetails")}`,
        terms: "One-time payment",
        steps: [`${t("pssSteps")}`],
        cta: {
          label: t("ctaPss"),
          actionKey: "pssWorkspace",
          state: { template_id: templateId, title: product.title, price },
        },
      });
    }
  }, [t, data, dataPSS, isMembershipRoute, isPSSRoute]);

  const handleRedirect = () => {
    if (!content?.cta?.actionKey) return;

    switch (content.cta.actionKey) {
      case "dashboard":
        navigate("/");
        break;
      case "pssWorkspace":
        navigate("/my-teaching", {
          state: {
            activeTab: "pills-pss",
            ...content.cta.state,
          },
        });
        break;
      default:
        navigate("/");
    }
  };

  const isLoadingFinal =
    (isMembershipRoute && isLoading) || (isPSSRoute && isLoadingPSS);
  const isError = (isMembershipRoute && error) || (isPSSRoute && errorPSS);

  if (isLoadingFinal) {
    return (
      <div className="container py-5 text-center">
        <Spinner
          animation="border"
          variant="primary"
          style={{ color: "var(--primaryColor)" }}
        />
        <p className="mt-3 text-muted">{t("loadingMessage")}</p>
      </div>
    );
  }

  if (isError || !content) {
    return (
      <div
        className="container d-flex flex-column justify-content-center align-items-center py-5"
        style={{ height: "100vh" }}
      >
        <h4 className="mb-3">{t("errorTitle")}</h4>
        <p className="text-muted">{t("errorMessage")}</p>
        <button className="site-btn mt-2" onClick={() => navigate("/")}>
          {t("goDashboard")}
        </button>
      </div>
    );
  }

  return (
    <>
      <Helmet>
        <title>Purchase Confirm | Traineego</title>
        <meta name="robots" content="noindex,follow" />
      </Helmet>
      <div
        className="container d-flex justify-content-center align-items-center py-5"
        style={{ minHeight: "100vh" }}
      >
        <div
          className="p-4 p-md-5 shadow text-center"
          style={{
            maxWidth: "600px",
            width: "100%",
            backgroundColor: "#151515",
            borderRadius: "12px",
          }}
        >
          <h3 className="mb-3">{content.title}</h3>
          <p className="text-muted mb-1">{content.details}</p>
          {content.terms && <p className="text-muted mb-2">{content.terms}</p>}
          {content.link && (
            <a
              href={content.link.url}
              target="_blank"
              rel="noreferrer"
              className="d-block mb-3 purple-text"
            >
              {content.link.text}
            </a>
          )}
          <div className="d-flex justify-content-center mb-4">
            {(() => {
              const mid = Math.ceil(content.steps.length / 2);
              const firstHalf = content.steps.slice(0, mid);
              const secondHalf = content.steps.slice(mid);

              return [firstHalf, secondHalf].map((half, idx) => (
                <ul key={idx} className="list-unstyled me-4">
                  {half.map((step, index) => (
                    <li
                      key={index}
                      className="mb-2 d-flex align-items-start text-start"
                    >
                      <div
                        className="rounded-circle me-2 mt-2"
                        style={{
                          backgroundColor: "#541ce2",
                          width: "10px",
                          height: "10px",
                        }}
                      />
                      {step}
                    </li>
                  ))}
                </ul>
              ));
            })()}
          </div>

          <button className="site-btn" onClick={handleRedirect}>
            {content.cta.label}
          </button>
        </div>
      </div>
    </>
  );
};

export default PurchaseConfirmation;
